import React, { useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { LayoutGrid } from "lucide-react";
import { FilterBar } from "./FilterBar";
import { GalleryCard } from "./GalleryCard";
import { LightboxModal } from "./LightboxModal";
import { TID } from "../../lib/testIds";

export const GallerySection = ({ structures, categories }) => {
  const [active, setActive] = useState("All");
  const [openIdx, setOpenIdx] = useState(null);

  const counts = useMemo(() => {
    const c = { All: structures.length };
    for (const cat of categories) {
      if (cat === "All") continue;
      c[cat] = structures.filter((s) => s.category === cat).length;
    }
    return c;
  }, [structures, categories]);

  const filtered = useMemo(() => {
    if (active === "All") return structures;
    return structures.filter((s) => s.category === active);
  }, [structures, active]);

  const handleOpen = (structure) => {
    // find index in filtered list
    const idx = filtered.findIndex((s) => s.id === structure.id);
    setOpenIdx(idx);
  };

  const handlePrev = () =>
    setOpenIdx((i) => (i === null ? null : (i - 1 + filtered.length) % filtered.length));
  const handleNext = () =>
    setOpenIdx((i) => (i === null ? null : (i + 1) % filtered.length));

  return (
    <section
      id="gallery"
      data-testid={TID.gallery.root}
      className="relative py-20 md:py-28 border-t border-slate-200/60"
    >
      <div className="px-6 md:px-10 lg:px-14 xl:px-16">
        {/* Section header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10 md:mb-14">
          <div>
            <div className="inline-flex items-center gap-2 mb-4 text-xs font-mono uppercase tracking-[0.22em] text-slate-500">
              <span className="w-8 h-px bg-[#0A2540]" />
              02 · The Gallery
            </div>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-semibold text-[#0A2540] tracking-tight leading-[1.08] max-w-2xl">
              Six structures.{" "}
              <span className="text-slate-400">One steel partner.</span>
            </h2>
            <p className="mt-4 text-base md:text-lg text-slate-600 max-w-xl leading-relaxed">
              Filter by structure type or browse the entire collection. Click any card
              to inspect specifications, use cases and delivery format.
            </p>
          </div>

          <div className="hidden md:flex items-center gap-2 text-xs font-mono uppercase tracking-[0.18em] text-slate-500 shrink-0">
            <LayoutGrid className="w-4 h-4" strokeWidth={2} />
            Uniform Grid
          </div>
        </div>

        {/* Filter bar */}
        <div className="mb-10">
          <FilterBar
            categories={categories}
            active={active}
            onChange={setActive}
            counts={counts}
          />
        </div>

        {/* Grid */}
        {filtered.length === 0 ? (
          <div
            data-testid={TID.gallery.empty}
            className="text-center py-24 border-2 border-dashed border-slate-200 rounded-2xl"
          >
            <p className="text-slate-500 font-medium">No structures found in this category.</p>
          </div>
        ) : (
          <motion.div
            layout
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8"
          >
            <AnimatePresence mode="popLayout">
              {filtered.map((s, idx) => (
                <GalleryCard
                  key={s.id}
                  structure={s}
                  index={idx}
                  onClick={() => handleOpen(s)}
                />
              ))}
            </AnimatePresence>
          </motion.div>
        )}
      </div>

      {/* Modal */}
      <LightboxModal
        structures={filtered}
        openIdx={openIdx}
        onClose={() => setOpenIdx(null)}
        onPrev={handlePrev}
        onNext={handleNext}
      />
    </section>
  );
};
